/**
 * Accessibility Statement Page
 *
 * Public page - accessible without authentication. Used as the Accessibility
 * URL for the CCMS mobile apps on the Apple App Store and Google Play. Not
 * linked from the login footer (reached directly by its URL).
 */

import {
  Stack,
  Group,
  SimpleGrid,
  Card,
  ThemeIcon,
  Title,
  Text,
  List,
  Anchor,
  Badge,
  rem,
} from '@mantine/core';
import {
  IconAccessible,
  IconEye,
  IconTextSize,
  IconContrast,
  IconKeyboard,
  IconAdjustmentsHorizontal,
  IconDeviceMobileCheck,
} from '@tabler/icons-react';
import PublicPageLayout from './PublicPageLayout';

const FEATURES = [
  {
    icon: IconEye,
    title: 'Screen readers',
    text: 'Screens, buttons and controls are labelled to work with VoiceOver (iOS), TalkBack (Android) and common web screen readers.',
  },
  {
    icon: IconTextSize,
    title: 'Text size & Dynamic Type',
    text: 'The apps respect your device font-size settings, and the web portal supports browser zoom and text resizing.',
  },
  {
    icon: IconContrast,
    title: 'Colour & contrast',
    text: 'We aim for sufficient contrast, offer light and dark themes, and never rely on colour alone to convey meaning.',
  },
  {
    icon: IconAdjustmentsHorizontal,
    title: 'Reduced motion',
    text: 'Where possible we honour the "reduce motion" setting on your device to minimise animation.',
  },
  {
    icon: IconKeyboard,
    title: 'Keyboard & focus',
    text: 'The web portal can be navigated with a keyboard, with visible focus indicators.',
  },
  {
    icon: IconDeviceMobileCheck,
    title: 'Works with your device',
    text: 'Built on the platform accessibility frameworks from Apple and Google so the apps work with the tools you already use.',
  },
];

const IN_APP = [
  'Text size — five preset sizes with a live preview',
  'Reduce motion — minimise animations',
  'Increase contrast — boost foreground/background separation',
  'Grayscale — render the interface without colour',
  'Bold text — a heavier text weight',
  'Strong focus outlines — always show outlines on focused controls',
  'Line & letter spacing — for easier reading',
];

export default function AccessibilityPage() {
  return (
    <PublicPageLayout
      eyebrow="CCMS Apps · Accessibility"
      title="Built to be usable by everyone"
      subtitle="Acts Christian Church is committed to making the CCMS apps usable by as many people as possible, including those who rely on assistive technologies. We aim to align with WCAG 2.1 Level AA on the web and build on the native accessibility of iOS and Android."
      icon={<IconAccessible size={28} />}
    >
      {/* Feature grid */}
      <Stack gap="lg">
        <Stack gap={4} align="center" ta="center">
          <Badge variant="light" radius="sm" size="lg">How we support you</Badge>
          <Title order={2} style={{ fontSize: rem(28), letterSpacing: '-0.3px' }}>Accessibility features</Title>
        </Stack>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {FEATURES.map((f) => (
            <Card key={f.title} withBorder radius="lg" padding="lg" shadow="sm">
              <ThemeIcon size={44} radius="md" variant="light"><f.icon size={22} /></ThemeIcon>
              <Text fw={700} mt="md">{f.title}</Text>
              <Text c="dimmed" size="sm" mt={4}>{f.text}</Text>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>

      {/* In-app settings highlight */}
      <Card withBorder radius="lg" padding="xl" bg="var(--mantine-color-indigo-light)">
        <Group align="flex-start" gap="lg" wrap="wrap">
          <ThemeIcon size={52} radius="md" variant="filled"><IconAdjustmentsHorizontal size={26} /></ThemeIcon>
          <Stack gap="xs" style={{ flex: 1, minWidth: rem(260) }}>
            <Title order={3} style={{ fontSize: rem(22) }}>In-app accessibility settings</Title>
            <Text c="dimmed" size="sm">
              Beyond your device’s system settings, the apps include a dedicated Accessibility screen
              (<b>Settings → Accessibility</b>) on iPhone, Android and the web portal. These controls
              layer on top of — never instead of — your device-wide preferences, which the apps
              continue to respect.
            </Text>
            <List size="sm" spacing={4} mt={4} c="dimmed">
              {IN_APP.map((item) => <List.Item key={item}>{item}</List.Item>)}
            </List>
          </Stack>
        </Group>
      </Card>

      {/* Ongoing work + reporting */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <Card withBorder radius="lg" padding="lg" shadow="sm">
          <Text fw={700} size="lg">Ongoing work</Text>
          <Text c="dimmed" size="sm" mt={4}>
            Accessibility is not a one-time task. As we add features we test with assistive
            technologies and fix issues we find. Some areas may not yet be fully optimised, and your
            feedback helps us prioritise improvements.
          </Text>
        </Card>
        <Card withBorder radius="lg" padding="lg" shadow="sm">
          <Text fw={700} size="lg">Report an accessibility issue</Text>
          <Text c="dimmed" size="sm" mt={4}>
            If you experience a barrier, tell us what happened, the device and assistive technology
            you used, and where it occurred. Contact{' '}
            <Anchor href="mailto:developer@actschurch.co.za">developer@actschurch.co.za</Anchor> — we’ll
            do our best to help and to fix it.
          </Text>
        </Card>
      </SimpleGrid>
    </PublicPageLayout>
  );
}
